import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, from } from 'rxjs';
import {
    CreateCredentialDefinitionWithAttestationRequest,
    CredentialDefinitionWithAttestation,
    Holder,
    IssuerAttestationDefinition,
    IssuerCredentialDefinition,
    IssuerOrganisationAttestationRequest,
    IssuerOrganisationAttestationWorkflow,
    JsonObject,
    JsonValue,
    VerifiableCredentialResource
} from '../models/issuer-api.models';
import { IssuerService } from './issuer.service';

/**
 * BIMSWARM projection of the Issuer Service catalogue. Every network call in
 * this facade goes directly to /issuerservice; the gateway adds the API key.
 */
@Injectable({ providedIn: 'root' })
export class IssuerCertificationCatalogueApiService {
    private readonly batchSize = 100;

    constructor(private readonly issuer: IssuerService) {}

    getCredentialDefinitions(
        participantContextId: string
    ): Observable<IssuerCredentialDefinition[]> {
        return from(this.loadCredentialDefinitions(participantContextId));
    }

    createCredentialDefinitionWithAttestation(
        participantContextId: string,
        request: CreateCredentialDefinitionWithAttestationRequest,
        issuerOrganisationId: number
    ): Observable<CredentialDefinitionWithAttestation> {
        return from(
            this.data(
                this.issuer.createCredentialDefinitionWithAttestation(
                    participantContextId,
                    request,
                    { issuerOrganisationId }
                )
            )
        );
    }

    getCredentialDefinition(
        participantContextId: string,
        credentialDefinitionId: string
    ): Observable<IssuerCredentialDefinition> {
        return from(
            this.data(
                this.issuer.getCredentialDefinition(
                    participantContextId,
                    credentialDefinitionId
                )
            )
        );
    }

    updateCredentialDefinition(
        participantContextId: string,
        definition: IssuerCredentialDefinition,
        issuerOrganisationId: number
    ): Observable<unknown> {
        return from(
            this.data(
                this.issuer.updateCredentialDefinition(
                    participantContextId,
                    definition,
                    { issuerOrganisationId }
                )
            )
        );
    }

    deleteCredentialDefinition(
        participantContextId: string,
        credentialDefinitionId: string,
        issuerOrganisationId: number
    ): Observable<void> {
        return from(
            this.complete(
                this.issuer.deleteCredentialDefinition(
                    participantContextId,
                    credentialDefinitionId,
                    { issuerOrganisationId }
                )
            )
        );
    }

    createOrganisationAttestation(
        participantContextId: string,
        credentialDefinitionId: string,
        request: IssuerOrganisationAttestationRequest,
        issuerOrganisationId: number
    ): Observable<IssuerOrganisationAttestationWorkflow> {
        return from(
            this.createOrganisationAttestationDirect(
                participantContextId,
                credentialDefinitionId,
                request,
                issuerOrganisationId
            )
        );
    }

    getOrganisationAttestations(
        participantContextId: string,
        credentialDefinitionId: string
    ): Observable<IssuerOrganisationAttestationWorkflow[]> {
        return from(
            this.loadOrganisationAttestations(
                participantContextId,
                credentialDefinitionId
            )
        );
    }

    getTrackedCredentials(
        participantContextId: string
    ): Observable<VerifiableCredentialResource[]> {
        return from(this.loadTrackedCredentials(participantContextId));
    }

    updateCredentialStatus(
        participantContextId: string,
        credentialId: string,
        action: 'revoke' | 'suspend' | 'resume',
        issuerOrganisationId: number,
        reason?: string
    ): Observable<VerifiableCredentialResource> {
        const response =
            action === 'revoke'
                ? this.issuer.revokeTrackedCredential(
                      participantContextId,
                      credentialId,
                      reason ?? '',
                      { issuerOrganisationId }
                  )
                : action === 'suspend'
                  ? this.issuer.suspendTrackedCredential(
                        participantContextId,
                        credentialId,
                        reason ?? '',
                        { issuerOrganisationId }
                    )
                  : this.issuer.resumeTrackedCredential(
                        participantContextId,
                        credentialId,
                        { issuerOrganisationId }
                    );
        return from(this.data(response));
    }

    private async createOrganisationAttestationDirect(
        participantContextId: string,
        credentialDefinitionId: string,
        request: IssuerOrganisationAttestationRequest,
        issuerOrganisationId: number
    ): Promise<IssuerOrganisationAttestationWorkflow> {
        const definition = await this.data(
            this.issuer.getCredentialDefinition(
                participantContextId,
                credentialDefinitionId
            )
        );
        const attestationId = (definition.attestations ?? []).find(Boolean);
        if (!attestationId) {
            throw new Error('Die Zertifikatsdefinition besitzt keine Attestierung.');
        }

        const holderId = this.resolveHolderDid(request);
        const holders = await this.loadHolders(participantContextId);
        if (!holders.some((holder) => this.holderId(holder) === holderId)) {
            await this.data(
                this.issuer.createHolder(
                    participantContextId,
                    {
                        holderId,
                        did: holderId,
                        name: request.organisationName,
                        properties: {
                            bimswarmOrganisationId: request.organisationId
                        }
                    },
                    { issuerOrganisationId }
                )
            );
        }
        await this.data(
            this.issuer.mergeAttestationConfiguration(
                participantContextId,
                attestationId,
                holderId,
                request.claims,
                { issuerOrganisationId }
            )
        );

        return {
            id: `${credentialDefinitionId}:${holderId}`,
            attestationId,
            participantContextId,
            credentialDefinitionId,
            organisationId: request.organisationId,
            organisationName: request.organisationName,
            holderId,
            status: 'CREATED',
            claims: request.claims,
            createdAt: new Date().toISOString()
        };
    }

    private async loadOrganisationAttestations(
        participantContextId: string,
        credentialDefinitionId: string
    ): Promise<IssuerOrganisationAttestationWorkflow[]> {
        const [definition, attestations, holders, tracked] = await Promise.all([
            this.data(
                this.issuer.getCredentialDefinition(
                    participantContextId,
                    credentialDefinitionId
                )
            ),
            this.data(this.issuer.getAllAttestations(participantContextId)),
            this.loadHolders(participantContextId),
            this.loadTrackedCredentials(participantContextId)
        ]);
        const definitionAttestations = new Set(definition.attestations ?? []);
        const relevantTracked = tracked.filter((credential) =>
            this.matchesDefinition(credential, definition)
        );
        const latestByHolder = new Map<string, VerifiableCredentialResource>();
        for (const credential of relevantTracked) {
            if (!credential.holderId) continue;
            const current = latestByHolder.get(credential.holderId);
            if (!current || this.createdAt(current) < this.createdAt(credential)) {
                latestByHolder.set(credential.holderId, credential);
            }
        }

        const workflows: IssuerOrganisationAttestationWorkflow[] = [];
        const assignedHolders = new Set<string>();
        for (const attestation of attestations.filter((item) =>
            definitionAttestations.has(item.id)
        )) {
            for (const [holderId, rawClaims] of Object.entries(
                attestation.configuration ?? {}
            )) {
                if (!holderId.startsWith('did:') || !this.isJsonObject(rawClaims)) {
                    continue;
                }
                assignedHolders.add(holderId);
                const holder = holders.find(
                    (candidate) => this.holderId(candidate) === holderId
                );
                workflows.push(
                    this.workflow(
                        participantContextId,
                        credentialDefinitionId,
                        attestation,
                        holderId,
                        rawClaims,
                        holder,
                        latestByHolder.get(holderId)
                    )
                );
            }
        }
        for (const credential of relevantTracked) {
            if (!credential.holderId || assignedHolders.has(credential.holderId)) {
                continue;
            }
            const holder = holders.find(
                (candidate) => this.holderId(candidate) === credential.holderId
            );
            workflows.push(
                this.workflow(
                    participantContextId,
                    credentialDefinitionId,
                    undefined,
                    credential.holderId,
                    {},
                    holder,
                    credential
                )
            );
        }
        return workflows.sort(
            (left, right) =>
                this.dateValue(right.createdAt) - this.dateValue(left.createdAt)
        );
    }

    private workflow(
        participantContextId: string,
        credentialDefinitionId: string,
        attestation: IssuerAttestationDefinition | undefined,
        holderId: string,
        claims: JsonObject,
        holder: Holder | undefined,
        tracked: VerifiableCredentialResource | undefined
    ): IssuerOrganisationAttestationWorkflow {
        return {
            id: tracked?.id ?? `${credentialDefinitionId}:${holderId}`,
            attestationId: attestation?.id,
            trackedCredentialId: tracked?.id,
            participantContextId,
            credentialDefinitionId,
            organisationId:
                tracked?.organisationId ?? this.organisationId(holder),
            organisationName: tracked?.holderName ?? holder?.holderName,
            holderId,
            status: tracked?.status ?? 'CREATED',
            claims,
            request: tracked?.request,
            credential: tracked?.credential,
            createdAt:
                tracked?.createdAt ?? this.epochToIso(holder?.createdAt),
            issuedAt: tracked?.issuedAt,
            revokedAt: tracked?.revokedAt,
            revocationReason: tracked?.revocationReason,
            suspensionReason: tracked?.suspensionReason,
            issuerCredentialId: tracked?.issuerCredentialId
        };
    }

    private async loadCredentialDefinitions(
        participantContextId: string
    ): Promise<IssuerCredentialDefinition[]> {
        return this.loadPaged((offset) =>
            this.data(
                this.issuer.queryCredentialDefinitions(participantContextId, {
                    limit: this.batchSize,
                    offset
                })
            )
        );
    }

    private async loadTrackedCredentials(
        participantContextId: string
    ): Promise<VerifiableCredentialResource[]> {
        return this.loadPaged((offset) =>
            this.data(
                this.issuer.queryTrackedCredentials(participantContextId, {
                    limit: this.batchSize,
                    offset
                })
            )
        );
    }

    private async loadHolders(participantContextId: string): Promise<Holder[]> {
        return this.loadPaged((offset) =>
            this.data(
                this.issuer.queryHolders(participantContextId, {
                    limit: this.batchSize,
                    offset
                })
            )
        );
    }

    private async loadPaged<T>(
        load: (offset: number) => Promise<T[]>
    ): Promise<T[]> {
        const all: T[] = [];
        for (let offset = 0; offset < 100000; offset += this.batchSize) {
            const page = await load(offset);
            all.push(...page);
            if (page.length < this.batchSize) return all;
        }
        throw new Error('Issuer Service pagination did not terminate.');
    }

    private async data<T>(response: Observable<{ data: T | null }>): Promise<T> {
        const result = await firstValueFrom(response);
        if (result.data == null) {
            throw new Error('Issuer Service returned no response data.');
        }
        return result.data;
    }

    private async complete(
        response: Observable<{ success: boolean; message?: string }>
    ): Promise<void> {
        const result = await firstValueFrom(response);
        if (!result.success) {
            throw new Error(result.message || 'Issuer Service request failed.');
        }
    }

    private matchesDefinition(
        credential: VerifiableCredentialResource,
        definition: IssuerCredentialDefinition
    ): boolean {
        const walletDefinitionParts = credential.credentialDefinitionId
            ?.split('/creddef/');
        const walletDefinitionType = walletDefinitionParts?.[
            walletDefinitionParts.length - 1
        ];
        const normalizedWalletType = this.normalizedIdentifier(
            walletDefinitionType
        );
        const normalizedCredentialType = this.normalizedIdentifier(
            definition.credentialType
        );
        return (
            credential.credentialDefinitionId === definition.id ||
            credential.issuerCredentialDefinitionId === definition.id ||
            credential.credentialDefinitionLabel === definition.credentialType ||
            (Boolean(normalizedWalletType) &&
                normalizedWalletType === normalizedCredentialType)
        );
    }

    private normalizedIdentifier(value?: string): string {
        return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    private resolveHolderDid(request: IssuerOrganisationAttestationRequest): string {
        if (request.holderDid?.startsWith('did:')) return request.holderDid;
        if (request.organisationId === 263) {
            return 'did:web:consumer-wallet:user:anull';
        }
        const value = request.organisationWebsite || request.organisationDomain;
        if (!value) {
            throw new Error(
                'Für die Organisation ist keine Institution-DID oder Website hinterlegt.'
            );
        }
        const url = new URL(value.includes('://') ? value : `https://${value}`);
        const path = url.pathname
            .split('/')
            .filter(Boolean)
            .map((segment) => `:${segment}`)
            .join('');
        const port = url.port ? `%3A${url.port}` : '';
        return `did:web:${url.hostname.toLowerCase()}${port}${path}`;
    }

    private holderId(holder: Holder): string {
        return holder.holderId || holder.did;
    }

    private organisationId(holder?: Holder): number | undefined {
        const value = holder?.properties?.['bimswarmOrganisationId'];
        return typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? Number(value)
              : undefined;
    }

    private isJsonObject(value: JsonValue): value is JsonObject {
        return value != null && typeof value === 'object' && !Array.isArray(value);
    }

    private createdAt(credential: VerifiableCredentialResource): number {
        return this.dateValue(credential.createdAt);
    }

    private dateValue(value?: string): number {
        return value ? new Date(value).getTime() || 0 : 0;
    }

    private epochToIso(value?: number): string | undefined {
        if (value == null) return undefined;
        return new Date(value < 100000000000 ? value * 1000 : value).toISOString();
    }
}
